// Pure TS Layer: React 非依存
// MQTT 接続を module-level singleton として保持し、React コンポーネントの
// マウント/アンマウントを越えて維持する。
//
// 設計意図:
//   - Monitor ページから他のページに移動しても接続は切れない (Sim 操作で発生する
//     publish もリアルタイムに受信できる)
//   - App 起動時に 1 度だけ初期化 (アイドルでも接続維持)
//   - 受信メッセージは Zustand ストアに直接 write (React と切り離し)
//   - 加えて外部購読者 (例: SessionStore) を addMessageListener で受け付ける

import { loadIotAuthConfig } from '@/hooks/auth/cognitoConfig';
import { useMqttMonitorStore } from '@/stores/mqttMonitor/mqttMonitorStore';
import {
  connectMqtt,
  type IncomingMessage,
  type MqttConnectionHandle,
} from './mqttClient';

type SingletonState = {
  handle: MqttConnectionHandle | null;
  initPromise: Promise<MqttConnectionHandle | null> | null;
  /** 現在 subscribe 中のトピックフィルタ群 */
  subscriptions: string[];
};

const state: SingletonState = {
  handle: null,
  initPromise: null,
  subscriptions: [],
};

// 受信メッセージリスナ (mqttMonitor 以外の購読者用、例: SessionStore)
type MessageListener = (msg: IncomingMessage) => void;
const messageListeners = new Set<MessageListener>();

/**
 * 受信メッセージのコールバックを登録する。
 * 戻り値の関数を呼ぶと解除される (useEffect の cleanup で使う想定)。
 */
export function addMessageListener(fn: MessageListener): () => void {
  messageListeners.add(fn);
  return () => {
    messageListeners.delete(fn);
  };
}

function dispatchMessage(msg: IncomingMessage): void {
  useMqttMonitorStore.getState().appendMessage(msg);
  for (const fn of messageListeners) {
    try {
      fn(msg);
    } catch {
      /* 個別 listener のエラーで他を止めない */
    }
  }
}

/**
 * MQTT 接続を確立 (既存ならそれを返す)。
 * App 起動時 / Monitor 初回マウント時に 1 度だけ呼ばれれば十分。
 */
export function ensureMqttConnection(): Promise<MqttConnectionHandle | null> {
  if (state.handle) return Promise.resolve(state.handle);
  if (state.initPromise) return state.initPromise;

  const store = useMqttMonitorStore.getState();
  const cfg = loadIotAuthConfig();
  if (!cfg.ok) {
    store.setConfigError(`環境変数が未設定: ${cfg.missing.join(', ')}`);
    store.setConnection('error', '設定エラー');
    return Promise.resolve(null);
  }

  store.setConnection('connecting', '初期化中');
  state.initPromise = connectMqtt(cfg.value, {
    onState: (st, detail) => {
      useMqttMonitorStore.getState().setConnection(st, detail ?? null);
    },
    onMessage: dispatchMessage,
  })
    .then((h) => {
      state.handle = h;
      state.initPromise = null;
      return h;
    })
    .catch((e) => {
      state.initPromise = null;
      useMqttMonitorStore
        .getState()
        .setConnection('error', e instanceof Error ? e.message : String(e));
      return null;
    });

  return state.initPromise;
}

/**
 * 購読中トピックを desired と一致させる (差分のみ subscribe/unsubscribe)。
 */
export async function syncSubscriptions(desired: ReadonlyArray<string>): Promise<void> {
  const handle = state.handle ?? (await ensureMqttConnection());
  if (!handle) return;

  const current = state.subscriptions;
  for (const t of current) {
    if (!desired.includes(t)) {
      try {
        await handle.unsubscribe(t);
      } catch {
        /* noop */
      }
    }
  }
  for (const t of desired) {
    if (!current.includes(t)) {
      try {
        await handle.subscribe(t);
      } catch (e) {
        useMqttMonitorStore
          .getState()
          .setConnection('error', e instanceof Error ? e.message : String(e));
      }
    }
  }
  state.subscriptions = [...desired];
}

/** テスト/開発用: 完全に切断してリセット */
export async function teardownMqttForTest(): Promise<void> {
  const h = state.handle;
  state.handle = null;
  state.initPromise = null;
  state.subscriptions = [];
  messageListeners.clear();
  if (h) await h.disconnect();
}
